import { z } from "zod";
import {
  preprocessNullableNumber,
  preprocessNullableInteger,
} from "utils/validation/zod-preprocessors";

// validation schemas for different age fields
export const AgeValidation = {
  chronologicalAge: z.preprocess(
    preprocessNullableInteger,
    z
      .number()
      .int("Age must be a whole number")
      .min(1, "Age must be at least 1 year")
      .max(150, "Age cannot exceed 150 years")
      .nullable()
      .or(z.literal(null)),
  ),

  ratedAge: z.preprocess(
    preprocessNullableInteger,
    z
      .number()
      .int("Rated age must be a whole number")
      .min(0, "Rated age must be at least 0")
      .max(150, "Rated age cannot exceed 150 years")
      .nullable()
      .or(z.literal(null)),
  ),

  lifeExpectancy: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .min(1.1, "Life expectancy must be greater than 1.0")
      .max(150, "Life expectancy cannot exceed 150 years")
      .nullable()
      .or(z.literal(null)),
  ),

  period: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .min(0, "Period cannot be negative")
      .max(999, "Period cannot exceed 999")
      .nullable()
      .or(z.literal(null)),
  ),

  lifeCompany: z
    .string()
    .min(1, "Life company name is required")
    .max(255, "Life company name cannot exceed 255 characters"),
};

// validation function that returns error message or null
export const validateAgeField = (
  field: string,
  value: unknown,
): string | null => {
  try {
    if (field === "claimant_chronological_age") {
      AgeValidation.chronologicalAge.parse(value);
    } else if (
      field === "rated_age_override" ||
      field === "median_rated_age" ||
      field === "active_rated_age" ||
      field === "rated_age"
    ) {
      AgeValidation.ratedAge.parse(value);
    } else if (
      field === "claimant_life_expectancy" ||
      field === "unrated_life_expectancy"
    ) {
      AgeValidation.lifeExpectancy.parse(value);
    } else if (field === "msa_period") {
      AgeValidation.period.parse(value);
    } else if (field === "life_company") {
      AgeValidation.lifeCompany.parse(value);
    }
    return null;
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.issues[0].message;
    }
    console.error("Validation error:", error);
    return "Please enter a valid value";
  }
};
